import React, { useState, useEffect } from "react";
import { api, eur, tokenParam, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import MonthSelector from "../components/MonthSelector";
import { MONTHS_IT } from "../lib/season";
import { FileText, Mail, Download, Loader2, Users, Wallet, Utensils, FileSpreadsheet, Recycle } from "lucide-react";
import { toast } from "sonner";

export default function Report() {
  const { canWrite } = useAuth();
  const { config } = useConfig();
  const [month, setMonth] = useState(6);
  const [rep, setRep] = useState(null);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [logType, setLogType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [xlsx, setXlsx] = useState(false);
  const [marpol, setMarpol] = useState(null);
  const [mpdl, setMpdl] = useState(false);

  const load = () => api.get(`/report/2026/${month}`).then((r) => setRep(r.data)).catch(() => {});
  useEffect(() => { load(); }, [month]);
  useEffect(() => {
    Promise.all([api.get("/marpol/inventory"), api.get("/marpol/entries", { params: { year: 2026 } })])
      .then(([inv, ent]) => {
        const onboard = inv.data.reduce((s, x) => s + (x.current_quantity_m3 || 0), 0);
        const reception = ent.data.filter((e) => e.operation_type === "discharge_to_reception").length;
        setMarpol({ onboard, count: ent.data.length, reception });
      }).catch(() => {});
  }, []);
  useEffect(() => {
    const params = {};
    if (logType !== "all") params.type = logType;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    api.get("/send-log", { params }).then((r) => setLog(r.data)).catch(() => {});
  }, [logType, fromDate, toDate]);

  const openPdf = () => {
    window.open(`${API}/report/2026/${month}/pdf?auth=${encodeURIComponent(tokenParam())}`, "_blank");
  };

  const sendEmail = async () => {
    if (!config?.owner_email) return toast.error("Configura l'email dell'Armatore in Impostazioni");
    setSending(true);
    try {
      const { data } = await api.post(`/report/2026/${month}/send-email`);
      toast.success(`Report inviato a ${data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  const downloadExcel = async () => {
    setXlsx(true);
    try {
      const res = await api.get("/export/foodcost.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "food_cost.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download Excel non riuscito");
    } finally {
      setXlsx(false);
    }
  };

  const downloadMarpol = async () => {
    setMpdl(true);
    try {
      const res = await api.get("/marpol/export/annual-summary", { params: { year: 2026 }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MARPOL-Sintesi-2026.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download sintesi MARPOL non riuscito");
    } finally {
      setMpdl(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Report Armatore</h1>
        <p className="text-slate-500">Riepilogo mensile · esportabile PDF e via email</p>
      </div>

      <MonthSelector month={month} onChange={setMonth} />

      <div className="flex flex-wrap gap-3">
        <button data-testid="view-pdf-btn" onClick={openPdf} className="flex h-14 items-center gap-2 rounded-xl border-2 border-navy px-5 font-semibold text-navy hover:bg-navy/5">
          <Download className="h-5 w-5" /> Apri PDF
        </button>
        <button data-testid="export-excel-btn" onClick={downloadExcel} disabled={xlsx} className="flex h-14 items-center gap-2 rounded-xl border-2 border-navy px-5 font-semibold text-navy hover:bg-navy/5 disabled:opacity-60">
          {xlsx ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileSpreadsheet className="h-5 w-5" />} Excel food cost
        </button>
        {canWrite && (
          <button data-testid="send-email-btn" onClick={sendEmail} disabled={sending} className="flex h-14 items-center gap-2 rounded-xl bg-gold px-6 font-bold text-black hover:bg-gold-hover disabled:opacity-60">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />} Invia all'Armatore
          </button>
        )}
      </div>

      {!rep ? (
        <div className="h-64 animate-pulse rounded-xl bg-slate-200" />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat icon={Wallet} label="Food cost / persona / giorno" value={eur(rep.food_cost_per_cover)} highlight />
            <Stat icon={Users} label="Coperti totali" value={rep.total_covers} />
            <Stat icon={Utensils} label="Spesa totale" value={eur(rep.total_spend)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MiniStat label="Spesa food" value={eur(rep.food_spend)} />
            <MiniStat label="Spesa non-food" value={eur(rep.nonfood_spend)} />
            <MiniStat label="Note di credito" value={eur(rep.credit_notes)} />
            <MiniStat label="Fatture" value={rep.invoice_count} />
          </div>

          {rep.budget > 0 && (
            <div className="grid gap-4 sm:grid-cols-3" data-testid="report-budget">
              <MiniStat label="Budget mensile Armatore" value={eur(rep.budget)} />
              <MiniStat label="Scostamento" value={eur(rep.budget_remaining)} />
              <MiniStat label="% budget speso" value={`${rep.budget_pct}%`} />
            </div>
          )}

          {rep.inventory_used && (
            <div className={`rounded-lg border px-4 py-2.5 text-sm ${rep.consumption < 0 ? "border-amber-400 bg-amber-50 text-amber-800" : "border-gold/40 bg-gold/10 text-navy"}`}>
              {rep.consumption < 0
                ? <>Attenzione: consumo negativo ({eur(rep.consumption)}) — la giacenza a fine mese supera giacenza iniziale + acquisti. Verifica i valori.</>
                : <>Calcolo con giacenze mensili · consumo reale stimato: <b>{eur(rep.consumption)}</b></>}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 p-4 font-head text-lg font-semibold text-navy">Spesa per categoria</div>
            {rep.by_category.length === 0 ? (
              <p className="p-6 text-center text-slate-400">Nessuna spesa registrata</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {rep.by_category.map((c, i) => (
                    <tr key={i} data-testid={`report-cat-${i}`} className={i % 2 ? "bg-slate-50" : ""}>
                      <td className="p-3 font-medium text-navy">{c.name}</td>
                      <td className="p-3">
                        <span className={`rounded px-2 py-0.5 text-xs ${c.is_food ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                          {c.is_food ? "Food" : "Non-food"}
                        </span>
                      </td>
                      <td className="tnum p-3 text-right font-semibold text-navy">{eur(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {marpol && (
        <div data-testid="report-marpol" className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
          <div className="mb-3 flex items-center gap-2">
            <Recycle className="h-5 w-5 text-emerald-600" />
            <h3 className="font-head text-lg font-semibold text-navy">Sostenibilità · MARPOL</h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <MiniStat label="Rifiuti a bordo" value={`${marpol.onboard.toFixed(2)} m³`} />
            <MiniStat label="Operazioni registrate 2026" value={marpol.count} />
            <MiniStat label="Consegne a terra 2026" value={marpol.reception} />
          </div>
          <button data-testid="marpol-summary-btn" onClick={downloadMarpol} disabled={mpdl} className="mt-4 flex h-12 items-center gap-2 rounded-xl border-2 border-emerald-600 px-5 font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
            {mpdl ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />} Sintesi MARPOL 2026 (PDF)
          </button>
        </div>
      )}

      {(log.length > 0 || logType !== "all") && (
        <div data-testid="send-log" className="rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 p-4">
            <span className="font-head text-lg font-semibold text-navy">Storico invii</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {[["all", "Tutti"], ["report", "Report"], ["haccp_alert", "Scadenze"], ["budget_alert", "Budget"]].map(([k, l]) => (
                <button key={k} data-testid={`log-filter-${k}`} onClick={() => setLogType(k)} className={`h-9 rounded-lg px-3 text-xs font-semibold ${logType === k ? "bg-navy text-white" : "bg-slate-100 text-navy"}`}>{l}</button>
              ))}
              <input data-testid="log-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-xs outline-none focus:border-gold" title="Da" />
              <span className="text-xs text-slate-400">–</span>
              <input data-testid="log-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-xs outline-none focus:border-gold" title="A" />
            </div>
          </div>
          {log.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">Nessun invio per questo filtro.</p>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {log.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50 last:border-0">
                    <td className="p-3">
                      <span className={`rounded px-2 py-0.5 text-xs font-semibold ${l.type === "report" ? "bg-navy/10 text-navy" : l.type === "budget_alert" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {l.type === "report" ? "Report" : l.type === "budget_alert" ? "Budget" : "Scadenze"}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{l.detail}</td>
                    <td className="p-3 text-slate-500">{l.sent_to}</td>
                    <td className="tnum p-3 text-right text-slate-400">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-5 ${highlight ? "border-gold bg-gold/10" : "border-slate-200 bg-white"}`}>
      <Icon className={`mb-2 h-5 w-5 ${highlight ? "text-gold" : "text-navy"}`} />
      <div className="tnum font-head text-2xl font-bold text-navy">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="tnum font-head text-lg font-bold text-navy">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}
